import { TransportRequestError } from "@/transport"

import { logger } from "@/lib/logger"
import type { OverageAcceptanceRequest } from "@/types_api/backend/transport/overage_acceptance_request"

import type { PaidOverageReviewPort } from "../hooks/usePaidOverageReview"
import {
  verifyPaidOverageOriginalControls,
  verifyPaidOverageReceipt,
} from "../utils/paidOverageReceipt"
import {
  createPaidOverageReview,
  paidOverageCanonicalJson,
  paidOverageRequest,
  readPaidOverageReviewKey,
  verifyPaidOverageControls,
  verifyPaidOverageFingerprint,
  verifyPaidOverageScope,
} from "../utils/paidOverageReview"
import {
  connectPaidOverageTransport,
  type PaidOverageIdentity,
} from "./paidOverageAuth"

type Intent = Parameters<PaidOverageReviewPort["decide"]>[0]
type Snapshot = ReturnType<typeof readPaidOverageReviewKey>
type Connection = Awaited<ReturnType<typeof connectPaidOverageTransport>>
type Failure = ReturnType<PaidOverageReviewPort["classify"]>

const log = logger("PaidOverageReview")

class DecisionFailure extends Error {
  constructor(readonly kind: Failure) {
    super("The paid-overage decision could not be verified.")
  }
}

function classifyPostFailure(error: unknown): Failure {
  if (error instanceof TransportRequestError) {
    const code = error.detail?.code
    if (
      error.status === 409 &&
      (code === "acceptance_conflict" || code === "offer_unavailable")
    ) {
      return "conflict"
    }
    if (
      (error.status === 400 && code === "invalid_request") ||
      (error.status === 401 && code === "authentication_required") ||
      (error.status === 403 && code === "permission_denied") ||
      (error.status === 409 && code === "overage_acceptance_not_launched")
    ) {
      return "rejected"
    }
  }
  return "uncertain"
}

async function originalDecision(
  intent: Intent,
  identity: PaidOverageIdentity,
  scopeKey: string,
) {
  const snapshot = readPaidOverageReviewKey(
    intent.reviewKey,
    scopeKey,
    identity.actorId,
  )
  if (
    identity.expectedOrganizationId !== undefined &&
    identity.expectedOrganizationId !== snapshot.response.organization_id
  ) {
    throw new Error("The original organization changed.")
  }
  await verifyPaidOverageFingerprint(snapshot.response)
  return { snapshot, request: paidOverageRequest(snapshot, intent) }
}

async function readOriginal(
  connection: Connection,
  snapshot: Snapshot,
  request: OverageAcceptanceRequest,
) {
  const response =
    await connection.client.operations.getBillingOverageAcceptanceReceipt(
      request,
      connection.options,
    )
  connection.options.signal.throwIfAborted()
  verifyPaidOverageScope(connection.organizationId, response)
  if (response.current_controls !== null) {
    verifyPaidOverageControls(
      response.current_controls,
      connection.organizationId,
    )
  }
  verifyPaidOverageOriginalControls(
    snapshot,
    request,
    response.receipt,
    response.original_controls,
  )
  return {
    receipt: response.receipt,
    result: verifyPaidOverageReceipt(snapshot, request, response.receipt),
  }
}

export function createPaidOverageReviewTransport({
  identity,
  scopeKey,
  invalidate,
}: {
  identity: PaidOverageIdentity
  scopeKey: string
  invalidate: () => Promise<void>
}): PaidOverageReviewPort {
  async function originalConnection(snapshot: Snapshot, signal: AbortSignal) {
    signal.throwIfAborted()
    return connectPaidOverageTransport(
      {
        ...identity,
        expectedOrganizationId: snapshot.response.organization_id,
      },
      signal,
    )
  }

  return {
    async load(signal) {
      const connection = await connectPaidOverageTransport(identity, signal)
      const response =
        await connection.client.operations.getBillingOverageAcceptanceOffer(
          connection.options,
        )
      signal.throwIfAborted()
      verifyPaidOverageScope(connection.organizationId, response)
      if (response.availability === "available") {
        await verifyPaidOverageFingerprint(response)
      }
      signal.throwIfAborted()
      return createPaidOverageReview(
        scopeKey,
        identity.actorId,
        connection.organizationId,
        response,
      )
    },
    async decide(intent, signal) {
      let original: Awaited<ReturnType<typeof originalDecision>>
      try {
        original = await originalDecision(intent, identity, scopeKey)
      } catch {
        throw new DecisionFailure("rejected")
      }
      const { snapshot, request } = original
      let connection: Connection
      try {
        connection = await originalConnection(snapshot, signal)
      } catch {
        throw new DecisionFailure("uncertain")
      }
      let response
      try {
        response =
          await connection.client.operations.createBillingOverageAcceptance(
            request,
            connection.options,
          )
      } catch (error: unknown) {
        throw new DecisionFailure(classifyPostFailure(error))
      } finally {
        void Promise.resolve()
          .then(invalidate)
          .catch(() => {
            log.warn("Billing data could not be refreshed after the decision.")
          })
      }
      try {
        signal.throwIfAborted()
        verifyPaidOverageScope(connection.organizationId, response)
        verifyPaidOverageControls(response.controls, connection.organizationId)
        verifyPaidOverageReceipt(snapshot, request, response.receipt)
        const verified = await readOriginal(connection, snapshot, request)
        if (
          paidOverageCanonicalJson(verified.receipt) !==
          paidOverageCanonicalJson(response.receipt)
        ) {
          throw new Error("The immutable receipt changed between reads.")
        }
        return verified.result
      } catch {
        // A readback rejection cannot prove that a preceding POST did not commit.
        throw new DecisionFailure("uncertain")
      }
    },
    async recheck(intent, signal) {
      const { snapshot, request } = await originalDecision(
        intent,
        identity,
        scopeKey,
      )
      const connection = await originalConnection(snapshot, signal)
      return (await readOriginal(connection, snapshot, request)).result
    },
    classify: (error) =>
      error instanceof DecisionFailure ? error.kind : "uncertain",
  }
}
